import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { AppShell, PageHeader } from "../components/AppShell";
import { money, monthLabel, addMonth, DEMO_MONTH, fmtDateAU } from "../lib/format";
import { ChevronLeft, ChevronRight, Plus, Receipt, Paperclip, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Expenses() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [month, setMonth] = useState(DEMO_MONTH);

  const { data, isLoading } = useQuery({
    queryKey: ["expenses", month],
    queryFn: async () => (await api.get(`/expenses?month=${month}`)).data,
  });

  const total = (data || []).reduce((s, e) => s + e.amount_cents, 0);

  const remove = async (id) => {
    await api.delete(`/expenses/${id}`);
    qc.invalidateQueries();
    toast.success("Expense deleted");
  };

  return (
    <AppShell>
      <PageHeader title="Expenses" subtitle={`${money(total)} this month`} onBack={() => navigate("/money")} />
      <div className="px-5">
        <div className="df-card flex items-center justify-between px-2 py-2 mb-4">
          <button data-testid="expenses-month-prev" onClick={() => setMonth(addMonth(month, -1))}
            className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center df-pressable">
            <ChevronLeft size={20} className="text-slate-600" />
          </button>
          <span className="font-heading font-semibold text-slate-900">{monthLabel(month)}</span>
          <button data-testid="expenses-month-next" onClick={() => setMonth(addMonth(month, 1))}
            className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center df-pressable">
            <ChevronRight size={20} className="text-slate-600" />
          </button>
        </div>
      </div>

      <div className="px-5 space-y-3 df-fade-in">
        {isLoading ? (
          <div className="text-slate-400 text-sm">Loading…</div>
        ) : data?.length ? (
          data.map((e) => (
            <div key={e.id} className="df-card p-4" data-testid={`expense-${e.id}`}>
              <div className="flex items-start justify-between gap-3">
                <button type="button" onClick={() => navigate(`/money/expense/${e.id}`)} data-testid={`expense-edit-${e.id}`}
                  className="min-w-0 text-left df-pressable">
                  <div className="font-medium text-slate-900">{e.category}</div>
                  {e.description && <div className="text-xs text-slate-500 mt-0.5">{e.description}</div>}
                  <div className="text-xs text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
                    <span>{fmtDateAU(e.date)}</span>
                    {e.practice_name && <span>· {e.practice_name}</span>}
                    {e.gst_included && <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">GST incl</span>}
                    {e.receipt_path && ((e.receipt_content_type || "").startsWith("image/") ? (
                      <span className="inline-flex items-center gap-1 text-[#0D786A]"><Paperclip size={12} /> Photo</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[#0D786A]"><Paperclip size={12} /> Receipt PDF</span>
                    ))}
                  </div>
                </button>
                <div className="text-right shrink-0">
                  <div className="font-num font-bold text-slate-900">{money(e.amount_cents)}</div>
                  <button onClick={() => remove(e.id)} data-testid={`expense-delete-${e.id}`}
                    className="mt-1 text-slate-300 hover:text-red-500 df-pressable"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="df-card p-8 text-center">
            <span className="mx-auto h-12 w-12 rounded-full bg-[#E6F4F1] text-[#0D786A] flex items-center justify-center mb-3">
              <Receipt size={22} />
            </span>
            <div className="font-heading font-semibold text-slate-900 text-lg">No expenses yet</div>
            <p className="text-sm text-slate-500 mt-1">Track deductible expenses so they flow into your Accountant Pack.</p>
          </div>
        )}
      </div>

      <div className="h-[calc(12.5rem+env(safe-area-inset-bottom))] lg:h-24" aria-hidden />

      <button data-testid="add-expense-button" onClick={() => navigate("/money/expense/new")}
        className="fixed bottom-[calc(6rem+env(safe-area-inset-bottom))] lg:bottom-8 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-5 h-12 rounded-full
                   bg-[#0D786A] text-white font-semibold shadow-lg shadow-emerald-900/20 df-pressable">
        <Plus size={20} /> Add Expense
      </button>
    </AppShell>
  );
}
